// Badania w tle (deep-research, raport DD, nauka stylu, recenzja pisma) jako zadanie w tle + odpytywanie
// (karta ASYNC-5, 20.10.2026) - WSPOLNE dla obu UI. Strony nie sa ruszane: przelaczenie robi `lib/api.js`:
// `deepResearch`, `generateDDReport`, `learnWritingStyle`, `reviewDocument` zachowaly nazwy i ksztalt wyniku
// (`{ data }` jak z axiosa), ale przy wlaczonej fladze serwera ida trasa `/async` (`.../async` + `GET /badania/jobs/{id}`).
//
// Rdzen odpytywania to ten sam co w Kreatorze, poradach i umowach (`generacjaAsync.js`): blad sieci/bramy/5xx w trakcie
// odpytywania = pytaj dalej (NIGDY nie startuj drugi raz), 410 (wynik juz odebrany; recenzja, raport DD i profil stylu sa
// zapisane w bazie) i przekroczenie czasu klienta = `ECONNABORTED`, blad zadania = blad w ksztalcie axios
// (`err.response.data.detail` tekstem). Roznice wzgledem umow (`umowyAsync.js`):
//   * flaga to `badania_async` z `GET /flags` (osobna od `umowy_async`, `porada_async` i `kreator_async`),
//   * komunikaty o konflikcie dotycza badania w tle (jedno badanie naraz na uzytkownika, wszystkie cztery rodzaje),
//   * odpytywanie co 5 s (badania trwaja minuty).
// Start jest idempotentny PO STRONIE SERWERA (skrot danych): ponowny start z tymi samymi danymi dolacza do trwajacego
// badania (202 + `dolaczono`), wiec klient moze bezpiecznie ponowic start po bledzie bramy (do 2 razy); 409 oznacza
// trwajace badanie Z INNYMI danymi -> czytelny komunikat, formularz nietkniety.
import { czekajNaZadanie, czyBladBramy } from "./generacjaAsync";

export const INTERWAL_BADANIA_MS = 5000;
export const LICZBA_PONOWIEN_STARTU = 2;
export const PAUZA_PONOWIENIA_MS = 3000;

let pamiecFlagi = null;

/** Czy badania w tle idą trasami async (flaga z `GET /flags`). Błąd odczytu = false (klasycznie) i nie jest zapamiętywany. */
export async function czyBadaniaAsync(pobierzFlagi) {
  if (pamiecFlagi !== null) return pamiecFlagi;
  try {
    const res = await pobierzFlagi();
    pamiecFlagi = res?.data?.badania_async === true;
    return pamiecFlagi;
  } catch {
    return false;
  }
}

/** Do testów i do wymuszenia ponownego odczytu flagi. */
export function ustawPamiecFlagiBadaniaAsync(wartosc) {
  pamiecFlagi = wartosc;
}

const uspij = (ms) => new Promise((r) => setTimeout(r, ms));

/** 409 (trwa już badanie w tle z innymi danymi): czytelny komunikat zamiast obiektu `detail`, formularz zostaje. */
function bladKonfliktu(err) {
  const r = err.response;
  const szczegol = r?.data?.detail;
  const komunikat = (typeof szczegol === "string" ? szczegol : szczegol?.message)
    || "Inne badanie w tle już trwa. Poczekaj na jego zakończenie.";
  err.response = { ...r, data: { detail: `${komunikat} Twoje dane nie zostały usunięte.` } };
  return err;
}

/**
 * Start zadania; zwraca job_id albo `null`, gdy trasa async jest niedostępna (404 „Not Found” = flaga wyłączona po
 * stronie serwera). Błąd bramy/sieci przy starcie: ponów (serwer dołączy do już utworzonego zadania, jeśli poprzedni
 * start jednak je utworzył). Timeout klienta przy starcie (np. duży upload) NIE jest ponawiany.
 */
async function uruchom(start, opcje) {
  const ponowienia = opcje.ponowienia ?? LICZBA_PONOWIEN_STARTU;
  const pauza = opcje.pauzaPonowieniaMs ?? PAUZA_PONOWIENIA_MS;
  const spij = opcje.uspij ?? uspij;
  for (let proba = 0; ; proba++) {
    let res;
    try {
      res = await start();
    } catch (err) {
      const r = err?.response;
      if (r?.status === 404 && r?.data?.detail === "Not Found") return null; // flaga wyłączona po stronie serwera
      if (r?.status === 409) throw bladKonfliktu(err);
      if (czyBladBramy(err) && err?.code !== "ECONNABORTED" && proba < ponowienia) {
        await spij(pauza);
        continue;
      }
      throw err;
    }
    // Brak job_id w poprawnej odpowiedzi to błąd serwera, nie bramy: poza try, więc NIE jest ponawiany.
    const jobId = res?.data?.job_id;
    if (!jobId) throw new Error("Serwer nie zwrócił identyfikatora zadania badania.");
    return jobId;
  }
}

/**
 * Badanie w tle: gdy `asyncWlaczone` — jako zadanie w tle z odpytywaniem; inaczej (albo gdy trasa async jest
 * niedostępna) `klasycznie()`. Zwraca `{ data }` jak klasyczna trasa. `start()` → odpowiedź 202 z `job_id`;
 * `odczytaj(jobId)` → odpowiedź GET statusu.
 */
export async function badaniaAuto({ asyncWlaczone, start, odczytaj, klasycznie, ...opcje }) {
  if (!asyncWlaczone) return klasycznie();
  const jobId = await uruchom(start, opcje);
  if (jobId === null) {
    pamiecFlagi = false;
    return klasycznie();
  }
  return czekajNaZadanie(() => odczytaj(jobId), { interwalMs: INTERWAL_BADANIA_MS, ...opcje });
}
